import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth } from '@opendoor/auth-fe/context';
import { redactGraphqlRequestErrorForTelemetry } from 'components/api/graphqlRequestError';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import type { HubBuyingTourFeedback } from 'components/hub/useHubTourFeedbacks';
import { captureError } from 'helpers/captureError';

import type { TouredHomeInput } from './__generated__/hub-tour-feedback.zeus';
import { HubBuyerTourFeedbackSubmitDocument } from './__generated__/hub-tour-feedback.zeus';
import {
  isQuickTourFeedbackSentiment,
  TOUR_FEEDBACK_POSITIVE,
  TOUR_FEEDBACK_SOURCE_POST_TOUR_MODAL,
} from './tourFeedback';
import type { QuickTourFeedbackSentiment } from './tourFeedback';
import type { HubTour } from './useHomeTours';

export type TourFeedbackByAppointmentId = Partial<Record<string, QuickTourFeedbackSentiment>>;

const TOUR_FEEDBACK_SUBMIT_OPERATION = 'HubBuyerTourFeedbackSubmit';

function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError'
  );
}

function withFeedbackByAppointmentId(
  current: TourFeedbackByAppointmentId,
  viewingAppointmentId: string,
  sentiment: QuickTourFeedbackSentiment,
): TourFeedbackByAppointmentId {
  return {
    ...current,
    [viewingAppointmentId]: sentiment,
  };
}

function withoutFeedbackByAppointmentId(
  current: TourFeedbackByAppointmentId,
  viewingAppointmentId: string,
): TourFeedbackByAppointmentId {
  const next = { ...current };
  delete next[viewingAppointmentId];
  return next;
}

function hasAppointmentId(current: Record<string, unknown>, viewingAppointmentId: string): boolean {
  return Object.prototype.hasOwnProperty.call(current, viewingAppointmentId);
}

const getTouredHomeInput = (tour: HubTour): TouredHomeInput =>
  tour.listingId ? { listingId: tour.listingId } : { addressId: tour.addressId };

function buildFeedbackByAppointmentId(
  feedbacks: HubBuyingTourFeedback[],
): TourFeedbackByAppointmentId {
  const feedbackByAppointmentId: TourFeedbackByAppointmentId = {};

  for (const feedback of feedbacks) {
    if (feedback.viewingAppointmentId && isQuickTourFeedbackSentiment(feedback.sentiment)) {
      feedbackByAppointmentId[feedback.viewingAppointmentId] = feedback.sentiment;
    }
  }

  return feedbackByAppointmentId;
}

export function useTourFeedback(feedbacks: HubBuyingTourFeedback[]): {
  feedbackByAppointmentId: TourFeedbackByAppointmentId;
  submit(tour: HubTour, sentiment: QuickTourFeedbackSentiment): Promise<boolean>;
} {
  const { authentication } = useAuth();
  const [feedbackByAppointmentId, setFeedbackByAppointmentId] =
    useState<TourFeedbackByAppointmentId>({});
  const feedbackByAppointmentIdRef = useRef<TourFeedbackByAppointmentId>({});
  const submitControllersByTourId = useRef(new Map<string, AbortController>());

  const replaceFeedbackState = useCallback(function replaceFeedbackState(
    nextFeedbackByAppointmentId: TourFeedbackByAppointmentId,
  ) {
    feedbackByAppointmentIdRef.current = nextFeedbackByAppointmentId;
    setFeedbackByAppointmentId(nextFeedbackByAppointmentId);
  }, []);

  const abortPendingSubmit = useCallback(function abortPendingSubmit(tourId: string) {
    const pendingSubmit = submitControllersByTourId.current.get(tourId);
    if (!pendingSubmit) {
      return;
    }

    pendingSubmit.abort();
    submitControllersByTourId.current.delete(tourId);
  }, []);

  useEffect(function abortPendingSubmitsOnUnmount() {
    return function abortPendingSubmits() {
      // oxlint-disable-next-line react/exhaustive-deps -- react19-baseline: pre-existing, do not copy. Add the missing dependencies rather than suppressing
      for (const controller of submitControllersByTourId.current.values()) {
        controller.abort();
      }
      // oxlint-disable-next-line react/exhaustive-deps -- react19-baseline: pre-existing, do not copy. Add the missing dependencies rather than suppressing
      submitControllersByTourId.current.clear();
    };
  }, []);

  useEffect(
    function syncTourFeedbackHistory() {
      replaceFeedbackState(buildFeedbackByAppointmentId(feedbacks));
    },
    [feedbacks, replaceFeedbackState],
  );

  const submit = useCallback(
    async function submit(tour: HubTour, sentiment: QuickTourFeedbackSentiment) {
      if (authentication.state !== 'authenticated') {
        return false;
      }

      abortPendingSubmit(tour.id);
      const previousFeedbackByAppointmentId = feedbackByAppointmentIdRef.current;
      const previousFeedback = previousFeedbackByAppointmentId[tour.id];
      const hadPreviousFeedback = hasAppointmentId(previousFeedbackByAppointmentId, tour.id);

      replaceFeedbackState(
        withFeedbackByAppointmentId(feedbackByAppointmentIdRef.current, tour.id, sentiment),
      );

      const controller = new AbortController();
      submitControllersByTourId.current.set(tour.id, controller);
      const home = getTouredHomeInput(tour);

      function restorePreviousFeedback() {
        if (feedbackByAppointmentIdRef.current[tour.id] !== sentiment) {
          return;
        }

        replaceFeedbackState(
          hadPreviousFeedback && previousFeedback
            ? withFeedbackByAppointmentId(
                feedbackByAppointmentIdRef.current,
                tour.id,
                previousFeedback,
              )
            : withoutFeedbackByAppointmentId(feedbackByAppointmentIdRef.current, tour.id),
        );
      }

      try {
        const response = await requestZeusFromBrowser(
          HubBuyerTourFeedbackSubmitDocument,
          {
            input: {
              home,
              viewingAppointmentId: tour.id,
              sentiment,
              likedAspects: [],
              dislikedAspects: [],
              source: TOUR_FEEDBACK_SOURCE_POST_TOUR_MODAL,
              wantsSimilarHomes: sentiment === TOUR_FEEDBACK_POSITIVE,
            },
          },
          controller.signal,
        );
        if (controller.signal.aborted) {
          return false;
        }

        const feedback = response.buyerTourFeedbackSubmit;
        if (feedback.__typename !== 'TourFeedback') {
          if (feedback.__typename !== 'UnauthorizedError') {
            captureError(
              new Error(`Tour feedback submission returned ${feedback.__typename}`),
              TOUR_FEEDBACK_SUBMIT_OPERATION,
            );
          }
          restorePreviousFeedback();
          return false;
        }
        if (!isQuickTourFeedbackSentiment(feedback.sentiment)) {
          captureError(
            new Error('Tour feedback submission returned an unsupported sentiment'),
            TOUR_FEEDBACK_SUBMIT_OPERATION,
          );
          restorePreviousFeedback();
          return false;
        }

        replaceFeedbackState(
          withFeedbackByAppointmentId(
            feedbackByAppointmentIdRef.current,
            tour.id,
            feedback.sentiment,
          ),
        );
        return true;
      } catch (error) {
        if (controller.signal.aborted || isAbortError(error)) {
          return false;
        }

        captureError(redactGraphqlRequestErrorForTelemetry(error), TOUR_FEEDBACK_SUBMIT_OPERATION);
        restorePreviousFeedback();
        return false;
      } finally {
        if (submitControllersByTourId.current.get(tour.id) === controller) {
          submitControllersByTourId.current.delete(tour.id);
        }
      }
    },
    [abortPendingSubmit, authentication.state, replaceFeedbackState],
  );

  return {
    feedbackByAppointmentId,
    submit,
  };
}
